import PreviewMapsStore    from '@/TokamapEditor/stores/PreviewMapsStore';
import { RouteLeg }       from '@tokamap/core';
import { makeAutoObservable } from 'mobx';

/**
 * L'itinéraire en cours dans l'aperçu, quand il traverse plusieurs cartes. Un itinéraire est une
 * suite de tronçons (voir `findRouteAcrossDocuments`), un par carte : seul celui de la carte
 * affichée est tracé, les autres attendent qu'on passe sur la leur.
 *
 * Il survit à la navigation : revenir en arrière, ou s'écarter du trajet, ne l'abandonne pas —
 * c'est un plan, pas un parcours en train de se faire. Il n'est remplacé qu'en en demandant un
 * autre.
 */
export default class PreviewRouteStore {
	legs: RouteLeg[] = [];

	/** Tronçon affiché — celui de la carte à l'écran, tant qu'elle en a un (voir `focusMap`). */
	index = 0;

	/** Message d'échec, vide tant que rien n'a échoué. */
	error = '';

	/** Message neutre affiché pendant une attente, ex. le temps qu'une position se fasse connaître. */
	pending = '';

	/**
	 * Numéro de la demande en cours. Calculer un itinéraire n'est pas instantané — il peut attendre
	 * la position du visiteur —, et pendant ce temps celui-ci peut effacer l'itinéraire ou en
	 * demander un autre : sans ce numéro, le calcul abandonné finirait par aboutir et ressusciterait
	 * ce qu'on venait de quitter. Hors observation : aucun affichage n'en dépend.
	 */
	private _run = 0;

	constructor(private maps: PreviewMapsStore) {
		makeAutoObservable<PreviewRouteStore, 'maps' | '_run'>(this, { maps: false, _run: false });
	}

	/** Ouvre une demande et rend son numéro, à représenter à `isCurrent` après chaque attente. */
	begin(): number {
		return ++this._run;
	}

	/** Vrai tant que `run` est la demande en cours — faux dès qu'elle a été effacée ou remplacée. */
	isCurrent(run: number): boolean {
		return this._run === run;
	}

	get isActive(): boolean {
		return this.legs.length > 0;
	}

	get currentLeg(): RouteLeg | undefined {
		return this.legs[this.index];
	}

	/** Carte du tronçon suivant — c'est elle qu'annonce l'étape, pas le passage lui-même. */
	get nextMapLabel(): string | undefined {
		const nextLeg = this.legs[this.index + 1];

		return nextLeg && this.maps.maps.find(map => map.mapId === nextLeg.documentId)?.label;
	}

	/** Annonce une attente — remplace tout message d'échec, qui ne vaut plus pour ce qu'on relance. */
	wait(message: string) {
		this.pending = message;
		this.error = '';
	}

	start(legs: RouteLeg[]) {
		this.legs = legs;
		this.index = 0;
		this.error = '';
		this.pending = '';
	}

	fail(message: string) {
		this.legs = [];
		this.index = 0;
		this.error = message;
		this.pending = '';
	}

	clear() {
		// Fait abandonner une demande encore en cours : ce qu'elle rendra ne concerne plus personne.
		this._run++;
		this.legs = [];
		this.index = 0;
		this.error = '';
		this.pending = '';
	}

	/**
	 * Désigne le tronçon qui se déroule sur `mapId`, et dit s'il en existe un. Retient le plus proche
	 * du tronçon courant, pour qu'un itinéraire repassant par une même carte reprenne là où l'on en
	 * était plutôt qu'à sa première occurrence.
	 *
	 * Remplace une progression pas à pas : avancer et revenir en arrière ne sont que deux cas du
	 * même geste — arriver sur une carte et y reprendre le trajet là où il passe.
	 */
	focusMap(mapId: id): boolean {
		let nearest = -1;
		let nearestDistance = Infinity;

		this.legs.forEach((leg, index) => {
			if (leg.documentId !== mapId) return;

			const distance = Math.abs(index - this.index);

			if (distance < nearestDistance) {
				nearest = index;
				nearestDistance = distance;
			}
		});

		if (nearest === -1) return false;

		this.index = nearest;

		return true;
	}
}
